import { logger } from '@inboundx/shared/platform';
import type { Database } from '@inboundx/shared/types';

import { supabase } from '@/lib/supabase';

export type PinnedPage = Database['public']['Tables']['backoffice_pinned_pages']['Row'];
type PinnedPageInsert = Database['public']['Tables']['backoffice_pinned_pages']['Insert'];
type ReorderPinnedPage = Pick<PinnedPage, 'label' | 'path'>;

const TABLE = 'backoffice_pinned_pages';

/** All pins for a user, in display order. */
export async function fetchPinnedPages(userId: string): Promise<PinnedPage[]> {
    const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('user_id', userId)
        .order('position')
        .order('created_at');

    if (error) {
        logger.error('Error fetching pinned pages:', error);
        throw error;
    }
    return data ?? [];
}

/** Pin a page. New pins go to the end. No-op if already pinned (unique constraint). */
export async function insertPin(
    userId: string,
    path: string,
    label: string,
    position: number
): Promise<void> {
    const { error } = await supabase
        .from(TABLE)
        .upsert(
            { user_id: userId, path, label, position },
            { onConflict: 'user_id,path', ignoreDuplicates: true }
        );

    if (error) {
        logger.error('Error inserting pinned page:', error);
        throw error;
    }
}

export async function deletePin(userId: string, path: string): Promise<void> {
    const { error } = await supabase.from(TABLE).delete().eq('user_id', userId).eq('path', path);

    if (error) {
        logger.error('Error deleting pinned page:', error);
        throw error;
    }
}

export async function renamePin(userId: string, path: string, label: string): Promise<void> {
    const { error } = await supabase
        .from(TABLE)
        .update({ label })
        .eq('user_id', userId)
        .eq('path', path);

    if (error) {
        logger.error('Error renaming pinned page:', error);
        throw error;
    }
}

/** Persist a new order: position = index in orderedPaths. */
export async function reorderPins(userId: string, orderedPins: ReorderPinnedPage[]): Promise<void> {
    if (orderedPins.length === 0) return;

    const rows: PinnedPageInsert[] = orderedPins.map((pin, position) => ({
        user_id: userId,
        path: pin.path,
        label: pin.label,
        position,
    }));

    const { error } = await supabase.from(TABLE).upsert(rows, {
        onConflict: 'user_id,path',
    });

    if (error) {
        logger.error('Error reordering pinned pages:', error);
        throw error;
    }
}
